import * as PIXI from 'pixi.js';
import { IBubbleGrid } from 'js/bubbleCore/types';
import { EventType, IEvent, IHasEventHandlers, EventHandlers } from 'js/bubbleCore/events';
import { BubbleGameBoardView } from 'js/bubbleCore/view/BubbleGameBoardView';
import { spacePositionToPxPosition } from 'js/bubbleCore/view/utils/spacePositionToPxPosition';
import { gridHexToPxPosition } from 'js/bubbleCore/view/utils/gridHexToPxPosition';
import { isInGrid } from 'js/bubbleCore/utils/isInGrid';
import { loopGrid } from 'js/bubbleCore/utils/loopGrid';
import { gridHexToSpacePosition } from 'js/bubbleCore/utils/gridHexToSpacePosition';
import { WarningPillarView } from 'js/bubbleCore/view/entities/WarningPillarView';
import { clamp } from 'framework/utils/math-utils';

export class DeathLineView extends PIXI.Container implements IHasEventHandlers {
  public readonly eventHandlers: EventHandlers = {
    [EventType.GridOffsetChanged]: (e) => {
      this.updatePillarsAlpha();
    },
    [EventType.BubbleTransfered]: (e) => {

    },
  };

  private readonly gridModel: IBubbleGrid;
  private readonly pillarsContainer: PIXI.Container = new PIXI.Container();
  private readonly pillars: Map<number, WarningPillarView> = new Map();
  private readonly boardView: BubbleGameBoardView;
  private readonly gfx: PIXI.Graphics;

  constructor(boardView: BubbleGameBoardView) {
    super();

    this.boardView = boardView;
    this.gridModel = boardView.getData().grid;
    this.createPillars(boardView);
    
    this.gfx = this.addChild(new PIXI.Graphics());
  }

  private createPillars(boardView: BubbleGameBoardView): void {
    this.addChild(this.pillarsContainer);
    const positionsX = this.getPilarsPositionsX(boardView);
    const y = this.getDeathLinePxY(boardView);
    positionsX.forEach((x) => this.createPillar(x, y));

    // const tm = boardView.tweenManager;
    // tm.create(this.pillarsContainer)
    //   .to({alpha: 0}, 400, tm.easing.Sinusoidal.InOut, true, 0, -1, true);
  }

  private createPillar(x: number, y: number): void {
    const pillar = new WarningPillarView();
    pillar.position.set(x, y);
    this.pillarsContainer.addChild(pillar);
    this.pillars.set(x, pillar);
  }

  private updatePillarsAlpha(): void {
    const maxValue: Map<number, number> = new Map();

    loopGrid(this.gridModel, (bubble, col, row) => {
      const gridPos = gridHexToSpacePosition(col, row, this.gridModel);
      const pxPos = gridHexToPxPosition(this.gridModel, col, row, this.boardView.getHexPixelSize());
      if (this.pillars.has(pxPos.x)) {
        const offset = (this.gridModel.deathLineY * 0.5)
        const alphaValue = clamp((gridPos.y - offset) / (this.gridModel.deathLineY - offset), 0, 1);
        maxValue.set(pxPos.x, Math.max(alphaValue, maxValue.get(pxPos.x) || 0));
      }
    });

    maxValue.forEach((value, key) => this.pillars.get(key)!.setWarningLevel(value));
  }

  private getPilarsPositionsX(boardView: BubbleGameBoardView): number[] {
    const positionsX: number[] = [];
    const { grid } = boardView.getData();
    const hexPixelSize = boardView.getHexPixelSize();
    const maxCol = grid.limits.width;

    for (let col = 0; col < maxCol; col++) {
      const pos1 = gridHexToPxPosition(grid, col, 0, hexPixelSize).x;
      if (!positionsX.includes(pos1)) positionsX.push(pos1);
      const pos2 = gridHexToPxPosition(grid, col, 1, hexPixelSize).x;
      if (!positionsX.includes(pos2)) positionsX.push(pos2);
    }

    return positionsX;
  }

  private getDeathLinePxY(boardView: BubbleGameBoardView): number {
    return spacePositionToPxPosition(
      0,
      boardView.getData().grid.deathLineY,
      boardView.getHexPixelSize(),
    ).y;
  }

  public resize(): void {
    const positionsX = this.getPilarsPositionsX(this.boardView);
    const y = this.getDeathLinePxY(this.boardView);
    positionsX.forEach((x) => this.pillars.get(x)?.position.set(x, y));

    this.gfx.clear();
    this.gfx.beginFill(0xffffff, 1);
    this.gfx.drawRect(0, 0, this.boardView.getBoardPixelSize().width, 5);
    this.gfx.y = y;
  }
}